import React, { useState, useEffect } from 'react';
import { User, Save, Camera, Loader2, CheckCircle, Mail, Phone, Briefcase, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import DeleteDataModal from '../../components/DeleteDataModal';

/**
 * Profile Settings Page
 */
const ProfileSettings = () => {
  const { user, updateUser } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [showDeleteData, setShowDeleteData] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employee_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        employee_id: user.employee_id || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await authAPI.updateProfile({
        name: formData.name,
        phone: formData.phone,
        employee_id: formData.employee_id
      });
      updateUser(response.data);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  // Clear message after timeout
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <User size={28} className="text-teal-600" />
          Profile Settings
        </h1>
        <p className="text-gray-500 mt-1">
          Manage your personal information
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
          message.type === 'error'
            ? 'bg-red-50 border border-red-200 text-red-600'
            : 'bg-green-50 border border-green-200 text-green-600'
        }`}>
          <CheckCircle size={18} />
          {message.text}
        </div>
      )}

      {/* Avatar Section */}
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg ${
              user?.role?.toLowerCase() === 'admin'
                ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                : 'bg-gradient-to-br from-teal-500 to-teal-600'
            }`}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <button className="absolute -bottom-2 -right-2 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors">
              <Camera size={16} className="text-gray-600" />
            </button>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{user?.name || 'User'}</h3>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <span className={`inline-block mt-2 px-2.5 py-1 text-xs font-semibold rounded-full ${
              user?.role?.toLowerCase() === 'admin'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-teal-100 text-teal-700'
            }`}>
              {user?.role || 'User'}
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                placeholder="Enter your name"
                data-testid="profile-name-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <div className="relative">
              <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                placeholder="Enter phone number"
                data-testid="profile-phone-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID</label>
            <div className="relative">
              <Briefcase size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                placeholder="Enter employee ID"
                data-testid="profile-employee-id-input"
              />
            </div>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium disabled:opacity-50"
            data-testid="save-profile-btn"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            Save Changes
          </button>
        </div>
      </form>

      {/* Danger Zone — Admin only */}
      {isAdmin && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mt-4" data-testid="danger-zone">
          <h3 className="text-base font-bold text-red-700 flex items-center gap-2">
            <Trash2 size={18} /> Danger Zone
          </h3>
          <p className="text-sm text-red-600 mt-1 mb-4">
            Permanently delete data by module. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowDeleteData(true)}
            data-testid="delete-data-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            <Trash2 size={16} /> Delete Data
          </button>
        </div>
      )}

      <DeleteDataModal isOpen={showDeleteData} onClose={() => setShowDeleteData(false)} />
    </div>
  );
};

export default ProfileSettings;
